import type { AppContextEntry } from './types'

/**
 * Flat per-entry UTF-8 byte clamp applied by the client registry when an
 * entry is registered. One number, uniform, no type awareness — the only
 * guard against a pathological value, since the server never truncates.
 *
 * Half of the server's 1 KB soft admission budget so a single fat entry
 * cannot dominate the fenced block alone.
 */
export const APP_CONTEXT_ENTRY_MAX_BYTES = 512

function utf8ByteLength(text: string): number {
  // TextEncoder is available in every browser target we ship; avoid Buffer
  // so this module stays browser-safe.
  return new TextEncoder().encode(text).length
}

/**
 * Clamp a single entry's value to {@link APP_CONTEXT_ENTRY_MAX_BYTES}.
 * Keys are left intact (producers own short labels). Truncation appends
 * an ellipsis when bytes were dropped.
 */
export function clampAppContextEntry(entry: AppContextEntry): AppContextEntry {
  const key = entry.key
  const value = entry.value
  if (utf8ByteLength(value) <= APP_CONTEXT_ENTRY_MAX_BYTES) {
    return { key, value }
  }
  // Walk code points so we don't split a surrogate pair; stop at the last
  // whole character that still fits under the budget with room for "…".
  const ellipsis = '…'
  const budget = APP_CONTEXT_ENTRY_MAX_BYTES - utf8ByteLength(ellipsis)
  let out = ''
  for (const char of value) {
    const next = out + char
    if (utf8ByteLength(next) > budget) break
    out = next
  }
  return { key, value: out + ellipsis }
}

/** Clamp every entry in an offered list (preserves order). */
export function clampAppContextEntries(entries: readonly AppContextEntry[]): AppContextEntry[] {
  return entries.map(clampAppContextEntry)
}
